/**
 * AccountMenu – Single source of truth for account dropdown.
 * Used identically in SiteHeader, LandingHeader, and AppTopbar.
 *
 * Sections:
 *   1. User identity (name + email)
 *   2. Portal switch ("Du använder Farmly som") – hidden if only 1 portal
 *   3. Account links (Mitt konto, Sparat, Inbjudningar)
 *   4. "Tillbaka till Farmly" – only inside app portals
 *   5. Logga ut
 */
import { Link, useLocation } from "react-router-dom";
import {
  User as UserIcon,
  LogOut,
  Heart,
  Mail,
  Home,
  Sprout,
  PenLine,
  Shield,
  Check,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import type { AppMode } from "@/context/ActivePortalContext";

// ── Types ──
interface AccountMenuProps {
  /** Custom trigger element (button/avatar) */
  trigger: React.ReactNode;
  /** User display name */
  displayName: string;
  /** User email */
  email: string | null;
  /** Current active mode */
  activeMode: AppMode;
  /** Switch mode callback */
  onSetMode: (mode: AppMode) => void;
  /** Sign out callback */
  onSignOut: () => void;
  /** Portal access flags */
  isHost: boolean;
  isEditor: boolean;
  isAdmin: boolean;
  /** First producer name for context display */
  firstProducerName?: string | null;
  /** Pending invitation count */
  invitationCount?: number;
  /** Dropdown alignment */
  align?: "start" | "center" | "end";
}

// ── Helpers ──
function truncateEmail(email: string, max = 28): string {
  if (email.length <= max) return email;
  const [local, domain] = email.split("@");
  if (!domain) return email.substring(0, max) + "…";
  const keep = Math.max(3, max - domain.length - 2);
  return local.substring(0, keep) + "…@" + domain;
}

const ITEM_CLASS =
  "flex items-center justify-between cursor-pointer rounded-lg px-3 py-2.5 focus:bg-foreground/[0.04] data-[highlighted]:bg-foreground/[0.04]";

const LINK_ITEM_CLASS =
  "rounded-lg px-3 py-2.5 cursor-pointer focus:bg-foreground/[0.04] data-[highlighted]:bg-foreground/[0.04]";

export function AccountMenu({
  trigger,
  displayName,
  email,
  activeMode,
  onSetMode,
  onSignOut,
  isHost,
  isEditor,
  isAdmin,
  firstProducerName,
  invitationCount = 0,
  align = "end",
}: AccountMenuProps) {
  const { pathname } = useLocation();
  const isInsideApp = pathname.startsWith("/app/") || pathname.startsWith("/admin");

  // Show portal switch if user has more than just visitor
  const portalCount = 1 + (isHost ? 1 : 0) + (isEditor ? 1 : 0) + (isAdmin ? 1 : 0);
  const showPortalSwitch = portalCount > 1;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>

      <DropdownMenuContent
        align={align}
        className="w-64 rounded-xl border-border/40 bg-popover p-1.5 shadow-lg"
      >
        {/* ── 1. Identity ── */}
        <div className="px-3 py-3">
          <p className="text-sm font-medium text-foreground leading-tight">
            {displayName}
          </p>
          <p className="text-xs text-muted-foreground mt-1 leading-tight truncate">
            {email ? truncateEmail(email) : "Inloggad"}
          </p>
        </div>

        <DropdownMenuSeparator className="mx-1" />

        {/* ── 2. Portal switch ── */}
        {showPortalSwitch && (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground/70 font-normal flex items-center gap-1.5 px-3">
              Du använder Farmly som
            </DropdownMenuLabel>

            {/* Visitor – always */}
            <DropdownMenuItem
              onClick={() => onSetMode("visitor")}
              className={ITEM_CLASS}
            >
              <div className="flex items-center gap-2">
                <UserIcon className="h-4 w-4 text-muted-foreground" />
                <span>Besökare</span>
              </div>
              {activeMode === "visitor" && (
                <Check className="h-4 w-4 text-muted-foreground" />
              )}
            </DropdownMenuItem>

            {/* Host */}
            {isHost ? (
              <DropdownMenuItem
                onClick={() => onSetMode("host")}
                className={ITEM_CLASS}
              >
                <div className="flex items-center gap-2">
                  <Home className="h-4 w-4 text-muted-foreground" />
                  <span className="flex-1">Värd</span>
                  {firstProducerName && (
                    <span className="text-xs text-muted-foreground/70 ml-1 truncate max-w-[100px]">
                      · {firstProducerName}
                    </span>
                  )}
                </div>
                {activeMode === "host" && (
                  <Check className="h-4 w-4 text-muted-foreground" />
                )}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem asChild className={LINK_ITEM_CLASS}>
                <Link
                  to="/for-producers/qualify"
                  className="flex items-center gap-2 text-muted-foreground cursor-pointer"
                >
                  <Sprout className="h-4 w-4" />
                  <span>För värdar</span>
                </Link>
              </DropdownMenuItem>
            )}

            {/* Editor */}
            {isEditor && (
              <DropdownMenuItem
                onClick={() => onSetMode("editor")}
                className={ITEM_CLASS}
              >
                <div className="flex items-center gap-2">
                  <PenLine className="h-4 w-4 text-muted-foreground" />
                  <span>Redaktion</span>
                </div>
                {activeMode === "editor" && (
                  <Check className="h-4 w-4 text-muted-foreground" />
                )}
              </DropdownMenuItem>
            )}

            {/* Admin */}
            {isAdmin && (
              <DropdownMenuItem
                onClick={() => onSetMode("admin")}
                className={ITEM_CLASS}
              >
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <span>Admin</span>
                </div>
                {activeMode === "admin" && (
                  <Check className="h-4 w-4 text-muted-foreground" />
                )}
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator className="mx-1" />
          </>
        )}

        {/* ── 3. Account links ── */}
        <div className="py-0.5">
          <DropdownMenuItem asChild className={LINK_ITEM_CLASS}>
            <Link to="/account" className="flex items-center gap-2.5">
              <UserIcon className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Mitt konto</span>
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem asChild className={LINK_ITEM_CLASS}>
            <Link to="/sparat" className="flex items-center gap-2.5">
              <Heart className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Sparat</span>
            </Link>
          </DropdownMenuItem>

          {invitationCount > 0 && (
            <DropdownMenuItem asChild className={LINK_ITEM_CLASS}>
              <Link
                to="/account/invitations"
                className="flex items-center justify-between"
              >
                <span className="flex items-center gap-2.5">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Inbjudningar</span>
                </span>
                <Badge
                  variant="secondary"
                  className="ml-2 h-5 min-w-[20px] px-1.5 text-[10px] font-medium bg-muted text-muted-foreground"
                >
                  {invitationCount}
                </Badge>
              </Link>
            </DropdownMenuItem>
          )}

          {/* "För värdar" link when user has no producer and no portal switch shown */}
          {!showPortalSwitch && !isHost && (
            <DropdownMenuItem asChild className={LINK_ITEM_CLASS}>
              <Link
                to="/for-producers/qualify"
                className="flex items-center gap-2.5"
              >
                <Sprout className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">För värdar</span>
              </Link>
            </DropdownMenuItem>
          )}
        </div>

        <DropdownMenuSeparator className="mx-1" />

        {/* ── 4. "Tillbaka till Farmly" – only inside portals ── */}
        {isInsideApp && (
          <>
            <DropdownMenuItem asChild className={LINK_ITEM_CLASS}>
              <Link to="/" className="flex items-center gap-2.5">
                <Home className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Tillbaka till Farmly</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="mx-1" />
          </>
        )}

        {/* ── 5. Sign out ── */}
        <div className="py-0.5">
          <DropdownMenuItem
            onClick={onSignOut}
            className="rounded-lg px-3 py-2.5 cursor-pointer text-muted-foreground focus:bg-foreground/[0.04] focus:text-foreground data-[highlighted]:bg-foreground/[0.04]"
          >
            <LogOut className="mr-2.5 h-4 w-4" />
            <span className="text-sm">Logga ut</span>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
